/**
 * InviteAssignmentFields — optionale Direkt-Zuweisung beim Einladen von Owner/Reinigung/Leitung:
 * Firma (vererbt Grants) + direkte Property-Grants. Controlled via value/onChange, damit
 * der UserCreateDialog schlank bleibt. Lädt Firmen + Properties selbst.
 */
import { useQuery } from '@tanstack/react-query';
import { FormField, Select } from '@/shared/components';
import { useApi } from '@/shared/api';
import { companyKeys } from '@/features/team/queries';
import { PropertyPicker } from '@/features/team/PropertyPicker';
import { useAssignableProperties } from '@/features/team/useAssignableProperties';

export interface InviteAssignment {
  companyId: string;
  propertyIds: string[];
}

export function InviteAssignmentFields({
  value,
  onChange,
}: {
  value: InviteAssignment;
  onChange: (next: InviteAssignment) => void;
}) {
  const api = useApi();
  const companies = useQuery({
    queryKey: companyKeys.list({ page: 1, pageSize: 200 }),
    queryFn: () => api.companies.list({ page: 1, pageSize: 200 }),
  });
  const properties = useAssignableProperties();

  const toggle = (id: string) => {
    const next = value.propertyIds.includes(id)
      ? value.propertyIds.filter((p) => p !== id)
      : [...value.propertyIds, id];
    onChange({ ...value, propertyIds: next });
  };

  return (
    <div className="border-line/50 mt-1 flex flex-col gap-4 rounded-md border border-dashed p-4">
      <p className="text-ink-soft text-xs">
        Optional: Firma-Mitgliedschaft + Apartments direkt bei der Einladung zuweisen. Nachträglich
        über „Zuweisungen" änderbar.
      </p>
      <FormField label="Firma">
        <Select
          value={value.companyId}
          onChange={(e) => onChange({ ...value, companyId: e.target.value })}
        >
          <option value="">— keine —</option>
          {(companies.data?.items ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField label="Apartments (direkt)">
        <PropertyPicker
          properties={properties.data ?? []}
          selectedIds={value.propertyIds}
          onToggle={toggle}
        />
      </FormField>
    </div>
  );
}
